import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import { Mail, UserRound } from 'lucide-react';
import { register } from '../../api/auth';
import { getErrorMessage } from '../../api/errors';
import { pushDataLayer } from '../../lib/measure';
import { isValidPhone, joinPhone } from '../../lib/phone';
import { useSession } from '../../state/session';
import { Alert, Button, Field, Input } from '../../ui';
import { PasswordInput } from '../common/PasswordInput';
import { PhoneField } from '../common/PhoneField';
import styles from './auth.module.css';

const schema = Yup.object({
  firstname: Yup.string().trim().required('First name is required'),
  lastname: Yup.string().trim().required('Last name is required'),
  email: Yup.string().trim().email('Enter a valid email address').required('Email is required'),
  phone: Yup.string()
    .required('Phone number is required')
    .test('phone', 'Enter a valid phone number', function check(value) {
      return isValidPhone(this.parent.phoneCountry, value);
    }),
  password: Yup.string().min(8, 'Use at least 8 characters').required('Password is required'),
});

export function RegisterForm({ onSuccess, defaultEmail = '', lockEmail = false }) {
  const { signIn } = useSession();
  const mutation = useMutation({
    mutationFn: register,
    onSuccess: (user) => {
      signIn(user);
      pushDataLayer({ event: 'account_created', method: 'email' });
      onSuccess?.(user);
    },
  });

  const formik = useFormik({
    initialValues: {
      firstname: '',
      lastname: '',
      email: defaultEmail,
      phoneCountry: 'US',
      phone: '',
      password: '',
    },
    validationSchema: schema,
    onSubmit: (values) =>
      mutation.mutate({
        firstname: values.firstname.trim(),
        lastname: values.lastname.trim(),
        email: values.email.trim(),
        phone: joinPhone(values.phoneCountry, values.phone),
        password: values.password,
      }),
  });

  const err = (name) => (formik.touched[name] && formik.errors[name]) || undefined;

  return (
    <form className={styles.form} onSubmit={formik.handleSubmit} noValidate>
      {mutation.isError && <Alert tone="danger">{getErrorMessage(mutation.error)}</Alert>}
      <div className={styles.row}>
        <Field label="First name" error={err('firstname')}>
          <Input autoComplete="given-name" leading={<UserRound />} {...formik.getFieldProps('firstname')} />
        </Field>
        <Field label="Last name" error={err('lastname')}>
          <Input autoComplete="family-name" {...formik.getFieldProps('lastname')} />
        </Field>
      </div>
      <Field label="Email" error={err('email')} hint={lockEmail ? 'This invite is tied to this email.' : undefined}>
        <Input
          type="email"
          autoComplete="email"
          inputMode="email"
          leading={<Mail />}
          readOnly={lockEmail}
          {...formik.getFieldProps('email')}
        />
      </Field>
      <Field label="Phone" error={err('phone')}>
        <PhoneField
          country={formik.values.phoneCountry}
          number={formik.values.phone}
          onCountryChange={(code) => formik.setFieldValue('phoneCountry', code)}
          onNumberChange={(value) => formik.setFieldValue('phone', value)}
          onBlur={() => formik.setFieldTouched('phone', true)}
        />
      </Field>
      <Field label="Password" error={err('password')} hint="At least 8 characters.">
        <PasswordInput autoComplete="new-password" {...formik.getFieldProps('password')} />
      </Field>
      <Button type="submit" size="lg" block loading={mutation.isPending}>
        Create account
      </Button>
      <p className={styles.legalNote}>
        By creating an account you agree to our <a href="/terms">Terms</a> and{' '}
        <a href="/privacy">Privacy Policy</a>.
      </p>
    </form>
  );
}
